import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DollarSign,
  Activity,
  Zap,
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Download,
  RefreshCw,
  Database,
  ChevronUp,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';
import { StatCard } from '../../../components/admin/StatCard';
import { LoadingSkeleton } from '../../../components/admin/LoadingSkeleton';
import {
  adminApi,
  type AnalyticsOverview,
  type CostByDayEntry,
  type AnalyticsBreakdown,
  type AnalyticsPerformance,
  type RateCardResponse,
  type LetterCostsResponse,
} from '../../../lib/api';
// Local definitions to avoid cross-package import issues in Docker builds
type PipelineStageId =
  | 'extraction'
  | 'correspondent_profile'
  | 'entity_resolution'
  | 'connection_enrichment'
  | 'summary_generation'
  | 'evolve_pre'
  | 'world_deepen'
  | 'daily_plan'
  | 'letter_write'
  | 'letter_polish'
  | 'consistency_check'
  | 'letter_subject'
  | 'letter_review'
  | 'embedding'
  | 'confirmation'
  | 'evolve_post';

type PipelineStageMeta = {
  label: string;
  order: number;
  group: number;
  color: string;
};

const PIPELINE_STAGE_META: Record<PipelineStageId, PipelineStageMeta> = {
  extraction: { label: 'Extract', order: 0, group: 0, color: 'bg-blue-500' },
  correspondent_profile: { label: 'Profile', order: 1, group: 0, color: 'bg-sky-500' },
  entity_resolution: { label: 'Resolve', order: 2, group: 0, color: 'bg-sky-400' },
  connection_enrichment: { label: 'Enrich', order: 3, group: 0, color: 'bg-purple-400' },
  summary_generation: { label: 'Summaries', order: 4, group: 0, color: 'bg-purple-300' },
  evolve_pre: { label: 'Evolve (Pre)', order: 5, group: 1, color: 'bg-purple-500' },
  world_deepen: { label: 'Deepen', order: 6, group: 1, color: 'bg-indigo-500' },
  daily_plan: { label: 'Plan', order: 7, group: 2, color: 'bg-green-400' },
  letter_write: { label: 'Write', order: 8, group: 2, color: 'bg-green-500' },
  letter_polish: { label: 'Polish', order: 9, group: 2, color: 'bg-green-600' },
  consistency_check: { label: 'Check', order: 10, group: 2, color: 'bg-green-600' },
  letter_subject: { label: 'Subject', order: 11, group: 2, color: 'bg-emerald-500' },
  letter_review: { label: 'Review', order: 12, group: 2, color: 'bg-emerald-400' },
  embedding: { label: 'Embed', order: 13, group: 3, color: 'bg-emerald-400' },
  confirmation: { label: 'Confirm', order: 14, group: 3, color: 'bg-emerald-300' },
  evolve_post: { label: 'Evolve (Post)', order: 15, group: 4, color: 'bg-orange-500' },
};

type DateRange = '7d' | '30d' | '90d' | 'mtd' | 'custom';
type GroupBy = 'model' | 'feature' | 'provider' | 'world';
type SplitBy = 'feature' | 'model' | 'provider' | 'none';
type Tab = 'overview' | 'breakdown' | 'performance' | 'letters' | 'rates' | 'operations';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCurrency(n: number): string {
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

/**
 * Format cost for display with appropriate precision
 * - Under $0.01: show 4 decimals (e.g., $0.0023)
 * - Under $0.10: show 3 decimals (e.g., $0.045)
 * - Otherwise: show 2 decimals (e.g., $0.15)
 */
function formatCostPrecise(n: number): string {
  if (n < 0.001) return `$${n.toFixed(4)}`;
  if (n < 0.01) return `$${n.toFixed(3)}`;
  if (n < 0.1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

const VALID_TABS: Tab[] = [
  'overview',
  'breakdown',
  'performance',
  'letters',
  'rates',
  'operations',
];

export function AnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [groupBy, setGroupBy] = useState<GroupBy>('feature');
  const [costSplitBy, setCostSplitBy] = useState<SplitBy>('feature');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get active tab from URL, default to "overview"
  const tabParam = searchParams.get('tab');
  const activeTab: Tab = VALID_TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'overview';

  const setActiveTab = (tab: Tab) => {
    setSearchParams({ tab });
  };

  // Data state
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [costByDay, setCostByDay] = useState<CostByDayEntry[]>([]);
  const [costDimensions, setCostDimensions] = useState<string[] | null>(null);
  const [breakdown, setBreakdown] = useState<AnalyticsBreakdown | null>(null);
  const [performance, setPerformance] = useState<AnalyticsPerformance | null>(null);

  // Calculate date range params
  const dateParams = useMemo(() => {
    const now = new Date();
    const to = now.toISOString();

    switch (dateRange) {
      case '7d': {
        const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return { from: from.toISOString(), to };
      }
      case '30d': {
        const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return { from: from.toISOString(), to };
      }
      case '90d': {
        const from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        return { from: from.toISOString(), to };
      }
      case 'mtd': {
        const from = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: from.toISOString(), to };
      }
      default:
        return { to };
    }
  }, [dateRange]);

  // Fetch data with polling
  useEffect(() => {
    async function fetchData(isInitial = false) {
      if (isInitial) {
        setIsLoading(true);
      }
      setError(null);
      try {
        const [overviewData, costData, breakdownData, perfData] = await Promise.all([
          adminApi.getAnalyticsOverview(dateParams),
          adminApi.getAnalyticsCostByDay({
            ...dateParams,
            splitBy: costSplitBy !== 'none' ? costSplitBy : undefined,
          }),
          adminApi.getAnalyticsBreakdown({ ...dateParams, groupBy }),
          adminApi.getAnalyticsPerformance(dateParams),
        ]);

        setOverview(overviewData);
        setCostByDay(costData.series);
        setCostDimensions(costData.dimensions);
        setBreakdown(breakdownData);
        setPerformance(perfData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        if (isInitial) {
          setIsLoading(false);
        }
      }
    }

    fetchData(true);

    // Poll every 10 seconds for live updates
    const interval = setInterval(() => fetchData(false), 10000);
    return () => clearInterval(interval);
  }, [dateParams, groupBy, costSplitBy]);

  // Export handler
  const handleExport = async () => {
    const { from, to } = dateParams;
    const url = `/api/admin/analytics/export?from=${from}&to=${to}`;
    window.open(url, '_blank');
  };

  // Manual refresh for danger zone clear
  const refreshAll = async () => {
    setIsLoading(true);
    try {
      const [overviewData, costData, breakdownData, perfData] = await Promise.all([
        adminApi.getAnalyticsOverview(dateParams),
        adminApi.getAnalyticsCostByDay({
          ...dateParams,
          splitBy: costSplitBy !== 'none' ? costSplitBy : undefined,
        }),
        adminApi.getAnalyticsBreakdown({ ...dateParams, groupBy }),
        adminApi.getAnalyticsPerformance(dateParams),
      ]);

      setOverview(overviewData);
      setCostByDay(costData.series);
      setCostDimensions(costData.dimensions);
      setBreakdown(breakdownData);
      setPerformance(perfData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="text-ember p-4 bg-ember/10 rounded-lg">
        <AlertTriangle className="inline mr-2" size={16} />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl text-parchment">LLM Analytics</h1>
            <span className="flex items-center gap-1.5 text-xs text-mist bg-stone/20 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Live
            </span>
          </div>
          <p className="text-mist text-sm mt-1">Usage, costs, and performance metrics</p>
        </div>

        {/* Date Range Selector */}
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRange)}
          className="bg-night border border-stone/30 rounded-lg px-3 py-2 text-parchment text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="mtd">Month to date</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          label="Total Cost"
          value={overview?.kpis.totalCost.formatted ?? '$0.00'}
          trend={overview?.kpis.totalCost.trend}
          icon={DollarSign}
          isLoading={isLoading}
        />
        <KPICard
          label="Requests"
          value={formatNumber(overview?.kpis.totalRequests.value ?? 0)}
          trend={overview?.kpis.totalRequests.trend}
          icon={Activity}
          isLoading={isLoading}
        />
        <KPICard
          label="Tokens"
          value={formatNumber(overview?.kpis.totalTokens.value ?? 0)}
          subtitle={`${formatNumber(overview?.kpis.totalTokens.inputTokens ?? 0)} in / ${formatNumber(overview?.kpis.totalTokens.outputTokens ?? 0)} out`}
          icon={Zap}
          isLoading={isLoading}
        />
        <KPICard
          label="Avg Latency"
          value={`${overview?.kpis.avgLatencyMs.value ?? 0}ms`}
          icon={Clock}
          isLoading={isLoading}
        />
        <KPICard
          label="Error Rate"
          value={`${overview?.kpis.errorRate.value ?? 0}%`}
          subtitle={`${overview?.kpis.errorRate.errorCount ?? 0} errors`}
          icon={AlertTriangle}
          variant={(overview?.kpis.errorRate.value ?? 0) > 5 ? 'error' : 'default'}
          isLoading={isLoading}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-stone/30">
        <nav className="flex gap-4">
          {(
            ['overview', 'breakdown', 'performance', 'letters', 'rates', 'operations'] as Tab[]
          ).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-rune text-rune'
                  : 'border-transparent text-mist hover:text-parchment'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          costByDay={costByDay}
          costDimensions={costDimensions}
          costSplitBy={costSplitBy}
          onSplitByChange={setCostSplitBy}
          breakdown={breakdown}
          isLoading={isLoading}
        />
      )}

      {activeTab === 'breakdown' && (
        <BreakdownTab
          breakdown={breakdown}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          isLoading={isLoading}
        />
      )}

      {activeTab === 'performance' && (
        <PerformanceTab performance={performance} isLoading={isLoading} />
      )}

      {activeTab === 'letters' && <LetterCostsTab dateParams={dateParams} />}

      {activeTab === 'rates' && <RatesTab />}

      {activeTab === 'operations' && (
        <OperationsTab onExport={handleExport} onClear={refreshAll} dateParams={dateParams} />
      )}
    </div>
  );
}

// KPI Card with trend indicator
function KPICard({
  label,
  value,
  subtitle,
  trend,
  icon: Icon,
  variant = 'default',
  isLoading,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  variant?: 'default' | 'error';
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <StatCard label={label} value="" isLoading />;
  }

  return (
    <div
      className={`bg-night border rounded-xl p-4 ${
        variant === 'error' ? 'border-ember/30' : 'border-stone/30'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-mist text-sm">{label}</div>
        <Icon size={16} className="text-mist/50" />
      </div>
      <div className="flex items-end gap-2">
        <div className="text-parchment text-2xl font-display">{value}</div>
        {trend !== undefined && trend !== 0 && (
          <div className={`flex items-center text-xs ${trend > 0 ? 'text-ember' : 'text-success'}`}>
            {trend > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            <span className="ml-0.5">
              {trend > 0 ? '+' : ''}
              {trend.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      {subtitle && <div className="text-mist/50 text-xs mt-1">{subtitle}</div>}
    </div>
  );
}

// Color palette for stacked bars
const STACK_COLORS = [
  'bg-rune',
  'bg-ember',
  'bg-success',
  'bg-amber-500',
  'bg-cyan-500',
  'bg-pink-500',
  'bg-violet-500',
  'bg-lime-500',
];

// Overview Tab with cost chart and top drivers
function OverviewTab({
  costByDay,
  costDimensions,
  costSplitBy,
  onSplitByChange,
  breakdown,
  isLoading,
}: {
  costByDay: CostByDayEntry[];
  costDimensions: string[] | null;
  costSplitBy: SplitBy;
  onSplitByChange: (splitBy: SplitBy) => void;
  breakdown: AnalyticsBreakdown | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSkeleton rows={8} />;
  }

  // Calculate max total cost per day for scaling
  const getMaxCost = () => {
    if (costDimensions && costDimensions.length > 0) {
      return Math.max(
        ...costByDay.map((day) =>
          costDimensions.reduce((sum, dim) => sum + (Number(day[dim]) || 0), 0),
        ),
        0.001,
      );
    }
    return Math.max(...costByDay.map((d) => d.cost), 0.001);
  };
  const maxCost = getMaxCost();

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Cost Trend Chart */}
      <div className="lg:col-span-2 bg-night border border-stone/30 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-parchment font-medium flex items-center gap-2">
            <BarChart3 size={16} className="text-rune" />
            Daily Cost Trend
          </h3>
          <select
            value={costSplitBy}
            onChange={(e) => onSplitByChange(e.target.value as SplitBy)}
            className="bg-void border border-stone/30 rounded px-2 py-1 text-sm text-parchment"
          >
            <option value="none">No split</option>
            <option value="feature">By Feature</option>
            <option value="model">By Model</option>
            <option value="provider">By Provider</option>
          </select>
        </div>

        {/* Legend */}
        {costDimensions && costDimensions.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            {costDimensions.map((dim, i) => (
              <div key={dim} className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded ${STACK_COLORS[i % STACK_COLORS.length]}`} />
                <span className="text-mist">{dim}</span>
              </div>
            ))}
          </div>
        )}

        <div className="h-48 flex items-end gap-1">
          {costByDay.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-mist text-sm">
              No data available
            </div>
          ) : costDimensions && costDimensions.length > 0 ? (
            // Stacked bar chart
            costByDay.slice(-30).map((day) => {
              const total = costDimensions.reduce((sum, dim) => sum + (Number(day[dim]) || 0), 0);
              return (
                <div
                  key={day.date as string}
                  className="flex-1 group relative flex flex-col justify-end"
                  style={{ height: '100%' }}
                >
                  <div
                    className="flex flex-col-reverse rounded-t overflow-hidden"
                    style={{ height: `${Math.max((total / maxCost) * 100, 2)}%` }}
                  >
                    {costDimensions.map((dim, i) => {
                      const value = Number(day[dim]) || 0;
                      const pct = total > 0 ? (value / total) * 100 : 0;
                      return (
                        <div
                          key={dim}
                          className={`${STACK_COLORS[i % STACK_COLORS.length]} opacity-70 hover:opacity-100 transition-opacity`}
                          style={{ height: `${pct}%` }}
                        />
                      );
                    })}
                  </div>
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-void border border-stone/50 rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                    <div className="text-parchment font-medium mb-1">{day.date}</div>
                    {costDimensions.map((dim) => (
                      <div key={dim} className="flex justify-between gap-3 text-mist">
                        <span>{dim}:</span>
                        <span className="text-parchment">
                          {formatCurrency(Number(day[dim]) || 0)}
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-stone/30 mt-1 pt-1 flex justify-between gap-3">
                      <span className="text-mist">Total:</span>
                      <span className="text-parchment">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            // Simple bar chart
            costByDay.slice(-30).map((day) => (
              <div
                key={day.date}
                className="flex-1 group relative"
                title={`${day.date}: ${formatCurrency(day.cost)}`}
              >
                <div
                  className="bg-rune/60 hover:bg-rune transition-colors rounded-t"
                  style={{
                    height: `${Math.max((day.cost / maxCost) * 100, 2)}%`,
                  }}
                />
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-void border border-stone/50 rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                  <div className="text-parchment">{formatCurrency(day.cost)}</div>
                  <div className="text-mist">{day.date}</div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex justify-between text-xs text-mist mt-2">
          <span>{costByDay[0]?.date ?? ''}</span>
          <span>{costByDay[costByDay.length - 1]?.date ?? ''}</span>
        </div>
      </div>

      {/* Top Cost Drivers */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <h3 className="text-parchment font-medium mb-4">Top Cost Drivers</h3>
        <div className="space-y-3">
          {breakdown?.items.slice(0, 5).map((item) => (
            <div key={item.key} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-parchment text-sm truncate">{item.key}</div>
                <div className="text-mist text-xs">{formatNumber(item.requests)} requests</div>
              </div>
              <div className="text-right">
                <div className="text-parchment text-sm">{formatCurrency(item.cost)}</div>
                <div className="text-mist text-xs">{item.percentage}%</div>
              </div>
            </div>
          ))}
          {(!breakdown || breakdown.items.length === 0) && (
            <div className="text-mist text-sm">No data available</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Breakdown Tab with grouping and table
function BreakdownTab({
  breakdown,
  groupBy,
  setGroupBy,
  isLoading,
}: {
  breakdown: AnalyticsBreakdown | null;
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
  isLoading: boolean;
}) {
  return (
    <div className="bg-night border border-stone/30 rounded-xl">
      {/* Header with group selector */}
      <div className="p-4 border-b border-stone/30 flex items-center justify-between">
        <h3 className="text-parchment font-medium">Cost Breakdown</h3>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as GroupBy)}
          className="bg-void border border-stone/30 rounded-lg px-3 py-1.5 text-parchment text-sm"
        >
          <option value="feature">By Feature</option>
          <option value="model">By Model</option>
          <option value="provider">By Provider</option>
          <option value="world">By World</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-4">
          <LoadingSkeleton rows={6} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-mist text-xs uppercase tracking-wide">
                <th className="text-left p-4">{groupBy === 'world' ? 'World' : groupBy}</th>
                <th className="text-right p-4">Cost</th>
                <th className="text-right p-4">%</th>
                <th className="text-right p-4">Requests</th>
                <th className="text-right p-4">Tokens</th>
                <th className="text-right p-4">Avg Latency</th>
              </tr>
            </thead>
            <tbody>
              {breakdown?.items.map((item) => (
                <tr key={item.key} className="border-t border-stone/20 hover:bg-stone/10">
                  <td className="p-4 text-parchment">{item.key}</td>
                  <td className="p-4 text-right text-parchment">{formatCurrency(item.cost)}</td>
                  <td className="p-4 text-right text-mist">{item.percentage}%</td>
                  <td className="p-4 text-right text-mist">{formatNumber(item.requests)}</td>
                  <td className="p-4 text-right text-mist">
                    {formatNumber(item.inputTokens + item.outputTokens)}
                  </td>
                  <td className="p-4 text-right text-mist">{item.avgLatencyMs}ms</td>
                </tr>
              ))}
              {(!breakdown || breakdown.items.length === 0) && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-mist text-sm">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
            {breakdown && breakdown.items.length > 0 && (
              <tfoot>
                <tr className="border-t border-stone/30 bg-stone/5">
                  <td className="p-4 text-parchment font-medium">Total</td>
                  <td className="p-4 text-right text-parchment font-medium">
                    {formatCurrency(breakdown.totalCost)}
                  </td>
                  <td className="p-4 text-right text-mist">100%</td>
                  <td className="p-4 text-right text-mist">
                    {formatNumber(breakdown.items.reduce((s, i) => s + i.requests, 0))}
                  </td>
                  <td className="p-4 text-right text-mist">
                    {formatNumber(
                      breakdown.items.reduce((s, i) => s + i.inputTokens + i.outputTokens, 0),
                    )}
                  </td>
                  <td className="p-4"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// Performance Tab with latency and errors
function PerformanceTab({
  performance,
  isLoading,
}: {
  performance: AnalyticsPerformance | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSkeleton rows={8} />;
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Latency by Model */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <h3 className="text-parchment font-medium mb-4">Latency by Model</h3>
        <div className="space-y-3">
          {performance?.latencyByModel.map((row) => (
            <div key={row.model} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-parchment truncate">{row.model}</span>
                <span className="text-mist">{row.avgLatencyMs}ms avg</span>
              </div>
              <div className="h-2 bg-stone/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-rune rounded-full"
                  style={{
                    width: `${Math.min((row.avgLatencyMs / 10000) * 100, 100)}%`,
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-mist/70">
                <span>min: {row.minLatencyMs}ms</span>
                <span>max: {row.maxLatencyMs}ms</span>
                <span>{formatNumber(row.requests)} req</span>
              </div>
            </div>
          ))}
          {(!performance || performance.latencyByModel.length === 0) && (
            <div className="text-mist text-sm">No data available</div>
          )}
        </div>
      </div>

      {/* Error Rates by Feature */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <h3 className="text-parchment font-medium mb-4">Error Rates</h3>
        <div className="space-y-3">
          {performance?.errorRates
            .filter((r) => r.errorCount > 0)
            .map((row) => (
              <div key={row.feature} className="flex items-center justify-between">
                <div>
                  <div className="text-parchment text-sm">{row.feature}</div>
                  <div className="text-mist text-xs">
                    {row.errorCount} / {formatNumber(row.totalRequests)} requests
                  </div>
                </div>
                <div
                  className={`text-sm font-medium ${row.errorRate > 5 ? 'text-ember' : 'text-amber-400'}`}
                >
                  {row.errorRate}%
                </div>
              </div>
            ))}
          {(!performance || performance.errorRates.every((r) => r.errorCount === 0)) && (
            <div className="text-success text-sm flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-success"></span>
              No errors in this period
            </div>
          )}
        </div>

        {/* Recent Errors */}
        {performance && performance.recentErrors.length > 0 && (
          <>
            <h4 className="text-parchment font-medium mt-6 mb-3 text-sm">Recent Errors</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {performance.recentErrors.slice(0, 10).map((err) => (
                <div
                  key={err.id}
                  className="bg-ember/10 border border-ember/20 rounded-lg p-2 text-xs"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-parchment">{err.feature}</span>
                    <span className="text-mist">{new Date(err.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="text-ember">{err.errorType ?? 'Unknown error'}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Letter Costs Tab - Per-letter pipeline costs
function LetterCostsTab({ dateParams }: { dateParams: { from?: string; to: string } }) {
  const [data, setData] = useState<LetterCostsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await adminApi.getLetterCosts({
          from: dateParams.from,
          to: dateParams.to,
        });
        setData(result);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load letter costs');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dateParams.from, dateParams.to]);

  const stageBreakdown = data?.stageBreakdown ?? [];

  const getStageMeta = (stage: string) => PIPELINE_STAGE_META[stage as PipelineStageId];
  const getStageLabel = (stage: string) => getStageMeta(stage)?.label ?? stage;
  const getStageColor = (stage: string) => getStageMeta(stage)?.color ?? 'bg-stone-500';
  const getStageOrder = (stage: string) => getStageMeta(stage)?.order ?? 99;
  const getStageGroup = (stage: string) => getStageMeta(stage)?.group ?? 99;

  const sortedStageBreakdown = useMemo(() => {
    const totalCost = stageBreakdown.reduce((sum, stage) => sum + stage.totalCost, 0);
    return [...stageBreakdown]
      .map((stage) => ({
        ...stage,
        percentage: totalCost > 0 ? Math.round((stage.totalCost / totalCost) * 10000) / 100 : 0,
      }))
      .sort((a, b) => getStageOrder(a.stage) - getStageOrder(b.stage));
  }, [stageBreakdown, getStageOrder]);

  const registryEntries = useMemo(
    () =>
      Object.entries(PIPELINE_STAGE_META)
        .map(([stage, meta]) => ({ stage, meta }))
        .sort((a, b) => a.meta.order - b.meta.order),
    [],
  );

  if (isLoading) {
    return <LoadingSkeleton rows={8} />;
  }

  if (error || !data) {
    return (
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        <div className="flex items-center gap-3 text-ember">
          <AlertTriangle size={20} />
          <span>{error ?? 'No letter cost data found'}</span>
        </div>
      </div>
    );
  }

  const { summary, packageBreakdown, distribution, recentLetters } = data;

  // Package display names and colors
  const packageConfig: Record<string, { label: string; color: string }> = {
    'aurora-premium': { label: 'Aurora Premium', color: 'bg-emerald-500' },
    'aurora-standard': { label: 'Aurora Standard', color: 'bg-emerald-400' },
    'aurora-basic': { label: 'Aurora Basic', color: 'bg-emerald-300' },
    'aurora-eco': { label: 'Aurora Eco', color: 'bg-emerald-200' },
    'forge-premium': { label: 'Forge Premium', color: 'bg-orange-500' },
    'forge-standard': { label: 'Forge Standard', color: 'bg-orange-400' },
    'forge-basic': { label: 'Forge Basic', color: 'bg-orange-300' },
    'forge-eco': { label: 'Forge Eco', color: 'bg-orange-200' },
    'quill-premium': { label: 'Quill Premium', color: 'bg-violet-500' },
    'quill-standard': { label: 'Quill Standard', color: 'bg-violet-400' },
    'quill-basic': { label: 'Quill Basic', color: 'bg-violet-300' },
    'quill-eco': { label: 'Quill Eco', color: 'bg-violet-200' },
    // Legacy aliases (pre-rename)
    'openai:premium': { label: 'Aurora Premium', color: 'bg-emerald-500' },
    'openai:standard': { label: 'Aurora Standard', color: 'bg-emerald-400' },
    'openai:basic': { label: 'Aurora Basic', color: 'bg-emerald-300' },
    'openai:eco': { label: 'Aurora Eco', color: 'bg-emerald-200' },
    'fireworks:premium': { label: 'Forge Premium', color: 'bg-orange-500' },
    'fireworks:standard': { label: 'Forge Standard', color: 'bg-orange-400' },
    'fireworks:basic': { label: 'Forge Basic', color: 'bg-orange-300' },
    'fireworks:eco': { label: 'Forge Eco', color: 'bg-orange-200' },
    'groq:premium': { label: 'Quill Premium', color: 'bg-violet-500' },
    'groq:standard': { label: 'Quill Standard', color: 'bg-violet-400' },
    'groq:basic': { label: 'Quill Basic', color: 'bg-violet-300' },
    'groq:eco': { label: 'Quill Eco', color: 'bg-violet-200' },
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Letters" value={summary.totalLetters.toString()} icon={Activity} />
        <StatCard
          label="Avg Cost / Letter"
          value={summary.avgCostPerLetterFormatted}
          icon={DollarSign}
          subtitle={`Min: $${summary.minCostPerLetter.toFixed(4)} / Max: $${summary.maxCostPerLetter.toFixed(4)}`}
        />
        <StatCard
          label="Avg Tokens / Letter"
          value={formatNumber(summary.avgTokensPerLetter)}
          icon={Zap}
        />
        <StatCard
          label="Avg Latency"
          value={`${(summary.avgLatencyMs / 1000).toFixed(1)}s`}
          icon={Clock}
        />
      </div>

      {/* Stage Breakdown */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <h3 className="text-parchment font-medium mb-4">Cost by Pipeline Stage</h3>
        <div className="space-y-3">
          {sortedStageBreakdown.length === 0 ? (
            <p className="text-mist text-sm">No stage data available</p>
          ) : (
            sortedStageBreakdown.map((stage) => {
              const label = getStageLabel(stage.stage);
              const color = getStageColor(stage.stage);
              return (
                <div key={stage.stage} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-parchment">{label}</span>
                    <span className="text-mist">
                      ${stage.avgCost.toFixed(4)} avg ({stage.percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="h-2 bg-stone/30 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${color} rounded-full transition-all`}
                      style={{ width: `${Math.min(stage.percentage, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-mist">
                    <span>{formatNumber(stage.totalTokens)} tokens</span>
                    <span>{stage.avgLatencyMs}ms avg</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Pipeline Registry */}
      <details className="bg-night border border-stone/30 rounded-xl p-4">
        <summary className="cursor-pointer text-parchment font-medium">
          Pipeline Registry
        </summary>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-mist text-left border-b border-stone/30">
                <th className="pb-2 font-medium">Stage</th>
                <th className="pb-2 font-medium">Label</th>
                <th className="pb-2 font-medium text-center">Order</th>
                <th className="pb-2 font-medium text-center">Group</th>
                <th className="pb-2 font-medium">Color</th>
              </tr>
            </thead>
            <tbody>
              {registryEntries.map(({ stage, meta }) => (
                <tr key={stage} className="border-b border-stone/20 last:border-0">
                  <td className="py-2 text-parchment font-mono text-xs">{stage}</td>
                  <td className="py-2 text-mist">{meta.label}</td>
                  <td className="py-2 text-center text-mist">{meta.order}</td>
                  <td className="py-2 text-center text-mist">{meta.group}</td>
                  <td className="py-2">
                    <span className={`inline-flex h-2 w-2 rounded-full ${meta.color}`} />
                    <span className="ml-2 text-mist text-xs">{meta.color}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* Package Breakdown */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <h3 className="text-parchment font-medium mb-4">Cost by LLM Package</h3>
        <div className="space-y-3">
          {packageBreakdown.length === 0 ? (
            <p className="text-mist text-sm">No package data available</p>
          ) : (
            packageBreakdown.map((pkg) => {
              const config = packageConfig[pkg.package] ?? {
                label: pkg.package,
                color: 'bg-stone-500',
              };
              return (
                <div key={pkg.package} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-parchment">{config.label}</span>
                    <span className="text-mist">
                      ${pkg.avgCost.toFixed(4)} avg ({pkg.percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="h-2 bg-stone/30 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${config.color} rounded-full transition-all`}
                      style={{ width: `${Math.min(pkg.percentage, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-mist">
                    <span>{formatNumber(pkg.totalTokens)} tokens</span>
                    <span>{pkg.avgLatencyMs}ms avg</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Cost Distribution */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <h3 className="text-parchment font-medium mb-4">Cost Distribution</h3>
        <div className="flex items-end gap-2 h-32">
          {distribution.map((bucket) => {
            const maxCount = Math.max(...distribution.map((d) => d.count));
            const height = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
            return (
              <div key={bucket.label} className="flex-1 flex flex-col items-center">
                <div className="w-full flex flex-col items-center justify-end h-24">
                  <span className="text-xs text-mist mb-1">{bucket.count}</span>
                  <div
                    className="w-full bg-rune/60 rounded-t transition-all"
                    style={{ height: `${height}%`, minHeight: bucket.count > 0 ? '4px' : '0' }}
                  />
                </div>
                <span className="text-xs text-mist mt-2 text-center">{bucket.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Letters */}
      <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-stone/30">
          <h3 className="text-parchment font-medium">Recent Letters</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-mist text-xs uppercase tracking-wide border-b border-stone/30">
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">World</th>
              <th className="text-right p-4">Tokens</th>
              <th className="text-right p-4">Latency</th>
              <th className="text-right p-4">Cost</th>
              <th className="text-left p-4">Stages</th>
              <th className="text-center p-4">Letter</th>
            </tr>
          </thead>
          <tbody>
            {recentLetters.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-mist text-sm">
                  No letters processed yet
                </td>
              </tr>
            ) : (
              recentLetters.map((letter) => (
                <tr key={letter.id} className="border-t border-stone/20 hover:bg-stone/10">
                  <td className="p-4 text-parchment text-sm">
                    {new Date(letter.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/worlds/${letter.worldId}`}
                        className="text-rune hover:text-rune/80 font-mono text-xs"
                        title="View world"
                      >
                        {letter.worldId}
                      </Link>
                      {letter.isQa && (
                        <span className="rounded-full border border-rune/40 bg-rune/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-rune">
                          QA
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-right text-mist text-sm">
                    {formatNumber(letter.totalTokens)}
                  </td>
                  <td className="p-4 text-right text-mist text-sm">
                    {(letter.totalLatencyMs / 1000).toFixed(1)}s
                  </td>
                  <td className="p-4 text-right">
                    {(() => {
                      const cost = letter.totalCost;
                      // Color by bracket: blue < $0.01, green < $0.03, yellow < $0.10, red < $0.20, purple >= $0.20
                      let colorClass = 'bg-blue-500/20 text-blue-400'; // < $0.01
                      if (cost >= 0.2) {
                        colorClass = 'bg-purple-500/20 text-purple-400';
                      } else if (cost >= 0.1) {
                        colorClass = 'bg-red-500/20 text-red-400';
                      } else if (cost >= 0.03) {
                        colorClass = 'bg-yellow-500/20 text-yellow-400';
                      } else if (cost >= 0.01) {
                        colorClass = 'bg-emerald-500/20 text-emerald-400';
                      }
                      return (
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colorClass} group relative cursor-default`}
                        >
                          {formatCostPrecise(cost)}
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs bg-obsidian text-parchment rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            ${cost.toFixed(6)}
                          </span>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      {(() => {
                        const normalizedStagesMap = new Map<
                          string,
                          { stage: string; cost: number; totalLatencyMs: number; count: number }
                        >();
                        for (const stage of letter.stages) {
                          const existing = normalizedStagesMap.get(stage.stage);
                          if (existing) {
                            existing.cost += stage.cost;
                            existing.totalLatencyMs += stage.latencyMs;
                            existing.count += 1;
                          } else {
                            normalizedStagesMap.set(stage.stage, {
                              stage: stage.stage,
                              cost: stage.cost,
                              totalLatencyMs: stage.latencyMs,
                              count: 1,
                            });
                          }
                        }

                        const sortedStages = Array.from(normalizedStagesMap.values()).sort(
                          (a, b) => getStageOrder(a.stage) - getStageOrder(b.stage),
                        );

                        return sortedStages.map((stage, idx) => {
                          const label = getStageLabel(stage.stage);
                          const color = getStageColor(stage.stage);
                          const nextStage = sortedStages[idx + 1];
                          const groupGap =
                            nextStage &&
                            getStageGroup(stage.stage) !== getStageGroup(nextStage.stage)
                              ? 'mr-2'
                              : '';
                          const avgLatencyMs =
                            stage.count > 0 ? Math.round(stage.totalLatencyMs / stage.count) : 0;
                          return (
                            <div
                              key={idx}
                              className={`w-2 h-2 rounded-full ${color} ${groupGap}`}
                              title={`${label}: $${stage.cost.toFixed(4)} · ${avgLatencyMs}ms avg`}
                            />
                          );
                        });
                      })()}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {letter.isQa && (
                        <span className="rounded-full border border-rune/40 bg-rune/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-rune">
                          QA
                        </span>
                      )}
                      {letter.letterInId && (
                        <Link
                          to={`/admin/letters-in/${letter.letterInId}`}
                          className="inline-flex items-center gap-1 text-rune hover:text-rune/80"
                          title="View inbound letter"
                        >
                          <ExternalLink size={12} />
                          <span className="text-xs">In</span>
                        </Link>
                      )}
                      {letter.letterOutId && (
                        <Link
                          to={`/admin/letters-out/${letter.letterOutId}`}
                          className="inline-flex items-center gap-1 text-rune hover:text-rune/80"
                          title="View outbound letter"
                        >
                          <ExternalLink size={12} />
                          <span className="text-xs">Out</span>
                        </Link>
                      )}
                      {!letter.letterInId && !letter.letterOutId && (
                        <span className="text-mist text-xs">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info Note */}
      {summary.avgLetterLength > 0 && (
        <div className="text-mist text-xs bg-stone/10 border border-stone/20 rounded-lg p-3">
          Average letter length: {summary.avgLetterLength} characters
          {summary.avgCostPerCharMicro && (
            <> &middot; ~${(summary.avgCostPerCharMicro / 1000000).toFixed(6)} per character</>
          )}
        </div>
      )}
    </div>
  );
}

// Rates Tab - LLM pricing from Helicone
type RateSortColumn = 'model' | 'input' | 'output';
type SortDirection = 'asc' | 'desc';

function RateSortIcon({
  column,
  sortColumn,
  sortDirection,
}: {
  column: RateSortColumn;
  sortColumn: RateSortColumn;
  sortDirection: SortDirection;
}) {
  if (sortColumn !== column) {
    return <ChevronUp size={14} className="opacity-30" />;
  }
  return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
}

function RatesTab() {
  const [rateData, setRateData] = useState<RateCardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeProvider, setActiveProvider] = useState<'openai' | 'fireworks' | 'groq' | 'other'>(
    'openai',
  );
  const [sortColumn, setSortColumn] = useState<RateSortColumn>('model');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const fetchRates = async () => {
    try {
      setError(null);
      const data = await adminApi.getRates();
      setRateData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rates');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await adminApi.triggerRateSync();
      // Wait a moment for the job to process
      setTimeout(async () => {
        await fetchRates();
        setIsSyncing(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to trigger sync:', err);
      setIsSyncing(false);
    }
  };

  const handleSort = (column: RateSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get current rates for the active provider (may be undefined if no data yet)
  const currentRates = rateData?.grouped[activeProvider];

  // useMemo must be called before any early returns (React hooks rules)
  const sortedRates = useMemo(() => {
    if (!currentRates) return [];
    const entries = Object.entries(currentRates);
    return entries.sort((a, b) => {
      const [modelA, rateA] = a;
      const [modelB, rateB] = b;

      let comparison = 0;
      switch (sortColumn) {
        case 'model':
          comparison = modelA.localeCompare(modelB);
          break;
        case 'input':
          comparison = rateA.inputPer1M - rateB.inputPer1M;
          break;
        case 'output':
          comparison = (rateA.outputPer1M ?? 0) - (rateB.outputPer1M ?? 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [currentRates, sortColumn, sortDirection]);

  if (isLoading) {
    return <LoadingSkeleton rows={10} />;
  }

  if (error || !rateData) {
    return (
      <div className="bg-night border border-stone/30 rounded-xl p-6">
        <div className="flex items-center gap-3 text-ember mb-4">
          <AlertTriangle size={20} />
          <span>{error ?? 'No rate card found'}</span>
        </div>
        <p className="text-mist text-sm mb-4">
          Rate cards are synced from Helicone on worker startup and hourly. Click below to trigger a
          manual sync.
        </p>
        <button
          onClick={handleSync}
          disabled={isSyncing}
          className="flex items-center gap-2 px-4 py-2 bg-rune/20 text-rune border border-rune/40 rounded-lg hover:bg-rune/30 transition-colors text-sm font-medium disabled:opacity-50"
        >
          <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
          {isSyncing ? 'Syncing...' : 'Sync from Helicone'}
        </button>
      </div>
    );
  }

  const { rateCard, providerCounts } = rateData;
  const providers = ['openai', 'fireworks', 'groq', 'other'] as const;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Database size={20} className="text-rune" />
            <div>
              <h3 className="text-parchment font-medium">Rate Card v{rateCard.version}</h3>
              <p className="text-mist text-xs">
                {rateCard.modelCount} models &middot; Synced from Helicone &middot; Last updated{' '}
                {new Date(rateCard.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-3 py-1.5 bg-rune/20 text-rune border border-rune/40 rounded-lg hover:bg-rune/30 transition-colors text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </div>

      {/* Provider Tabs */}
      <div className="flex gap-2">
        {providers.map((provider) => (
          <button
            key={provider}
            onClick={() => setActiveProvider(provider)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeProvider === provider
                ? 'bg-rune text-white'
                : 'bg-stone/20 text-mist hover:bg-stone/30 hover:text-parchment'
            }`}
          >
            {provider.charAt(0).toUpperCase() + provider.slice(1)}
            <span className="ml-1.5 text-xs opacity-70">({providerCounts[provider]})</span>
          </button>
        ))}
      </div>

      {/* Rate Table */}
      <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-mist text-xs uppercase tracking-wide border-b border-stone/30">
              <th
                className="text-left p-4 cursor-pointer hover:text-parchment transition-colors select-none"
                onClick={() => handleSort('model')}
              >
                <div className="flex items-center gap-1">
                  Model
                  <RateSortIcon
                    column="model"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                  />
                </div>
              </th>
              <th
                className="text-right p-4 cursor-pointer hover:text-parchment transition-colors select-none"
                onClick={() => handleSort('input')}
              >
                <div className="flex items-center justify-end gap-1">
                  Input / 1M
                  <RateSortIcon
                    column="input"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                  />
                </div>
              </th>
              <th
                className="text-right p-4 cursor-pointer hover:text-parchment transition-colors select-none"
                onClick={() => handleSort('output')}
              >
                <div className="flex items-center justify-end gap-1">
                  Output / 1M
                  <RateSortIcon
                    column="output"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRates.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-8 text-center text-mist text-sm">
                  No models for this provider
                </td>
              </tr>
            ) : (
              sortedRates.map(([model, rate]) => (
                <tr key={model} className="border-t border-stone/20 hover:bg-stone/10">
                  <td className="p-4 text-parchment text-sm font-mono">{model}</td>
                  <td className="p-4 text-right text-mist text-sm">
                    ${rate.inputPer1M.toFixed(4)}
                  </td>
                  <td className="p-4 text-right text-mist text-sm">
                    {rate.outputPer1M !== undefined ? `$${rate.outputPer1M.toFixed(4)}` : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info Note */}
      <div className="text-mist text-xs bg-stone/10 border border-stone/20 rounded-lg p-3">
        Rates are fetched from{' '}
        <a
          href="https://www.helicone.ai/api/llm-costs"
          target="_blank"
          rel="noopener noreferrer"
          className="text-rune hover:underline"
        >
          Helicone LLM Costs API
        </a>{' '}
        on worker startup and hourly thereafter. Database is the source of truth for cost
        calculations.
      </div>
    </div>
  );
}

function OperationsTab({
  onExport,
  onClear,
  dateParams,
}: {
  onExport: () => void;
  onClear: () => void;
  dateParams: { from?: string; to: string };
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState<{
    events: number;
    stages: number;
    letterCosts: number;
    rollups: number;
  } | null>(null);

  const handleClear = async () => {
    setClearing(true);
    setClearResult(null);
    try {
      const result = await adminApi.clearAnalytics();
      setClearResult(result.deleted);
      setShowConfirm(false);
      onClear();
    } catch (err) {
      console.error('Failed to clear analytics:', err);
      alert('Failed to clear analytics data. Check console for details.');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <div className="bg-night rounded-lg border border-stone/30 p-6">
        <h3 className="text-parchment font-medium text-lg mb-2">Export Data</h3>
        <p className="text-mist text-sm mb-4">
          Download analytics data as a CSV file for the selected date range.
        </p>
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-4 py-2 bg-rune/20 text-rune border border-rune/40 rounded-lg hover:bg-rune/30 transition-colors text-sm font-medium"
        >
          <Download size={16} />
          Export CSV ({dateParams.from?.split('T')[0] ?? 'all'} to {dateParams.to.split('T')[0]})
        </button>
      </div>

      {/* Danger Zone */}
      <div className="bg-ink rounded-lg border border-ember/30 p-6">
        <h3 className="text-ember font-medium text-lg mb-2">Danger Zone</h3>
        <p className="text-mist text-sm mb-4">
          Permanently delete all analytics data. This action cannot be undone.
        </p>

        {clearResult && (
          <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg text-sm text-success">
            Cleared: {clearResult.events} events, {clearResult.stages} stages,{' '}
            {clearResult.letterCosts} letter costs, {clearResult.rollups} rollups
          </div>
        )}

        {!showConfirm ? (
          <button
            onClick={() => setShowConfirm(true)}
            className="px-4 py-2 bg-ember/20 text-ember border border-ember/40 rounded-lg hover:bg-ember/30 transition-colors text-sm font-medium"
          >
            Clear All Analytics Data
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-ember text-sm">Are you sure?</span>
            <button
              onClick={handleClear}
              disabled={clearing}
              className="px-4 py-2 bg-ember text-white rounded-lg hover:bg-ember/80 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {clearing ? 'Clearing...' : 'Yes, Delete Everything'}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              disabled={clearing}
              className="px-4 py-2 bg-mist/10 text-parchment border border-mist/20 rounded-lg hover:bg-mist/20 transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;
